import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ComeUsareArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Come Usare Itaobuy Spreadsheet: Tutorial Completo Passo dopo Passo',
    description: 'Tutorial completo su come usare Itaobuy Spreadsheet: dalla scelta della categoria alla verifica delle foto QC. Guida italiana con esempi pratici per principianti ed esperti.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Come si inizia a usare Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Apri lo spreadsheet dal pulsante Itaobuy Sheet, scegli una categoria prodotto, esplora le schede con foto QC e link. È un processo intuitivo che richiede pochi minuti.' } },
      { '@type': 'Question', name: 'Devo creare un account per usare Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'No, puoi accedere allo spreadsheet e navigare tra le categorie senza registrazione. Per approfondire i prodotti, verrai reindirizzato alla piattaforma principale.' } },
      { '@type': 'Question', name: 'Quanto tempo ci vuole per imparare a usare Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Bastano 5-10 minuti per capire il funzionamento di base. Con la pratica, diventerai rapidissimo nel trovare esattamente ciò che cerchi.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Come Usare Itaobuy Spreadsheet', item: `${siteUrl}/itaobuy-spreadsheet-come-usare` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Come Usare Itaobuy Spreadsheet</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Tutorial
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Come Usare Itaobuy Spreadsheet:</span> Tutorial Passo dopo Passo
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              Se ti stai chiedendo <strong>come usare itaobuy spreadsheet</strong>, sei nel posto giusto. Questo tutorial ti guiderà attraverso ogni passaggio — dalla prima apertura alla scelta del prodotto perfetto — in modo semplice, chiaro e adatto sia ai principianti assoluti che a chi ha già un po&apos; di esperienza.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20hands%20typing%20on%20a%20laptop%20keyboard%20next%20to%20a%20notepad%20with%20handwritten%20step%20by%20step%20instructions%20and%20a%20smartphone%20displaying%20a%20product%20catalog%2C%20warm%20coffee%20shop%20table%20with%20a%20latte%20on%20the%20side%2C%20soft%20ambient%20lighting%2C%20tutorial%20and%20learning%20concept%2C%20authentic%20editorial%20photography%20with%20instructional%20aesthetic&width=1200&height=600&seq=article-comeusare-hero&orientation=landscape"
              alt="Come usare Itaobuy Spreadsheet tutorial"
              title="Come usare Itaobuy Spreadsheet"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              I 5 passi per usare <span className="text-tealwarm-600">Itaobuy Spreadsheet</span>
            </h2>
            <div className="mt-6 space-y-6">
              {[
                { step: 1, title: 'Accedi allo spreadsheet', desc: 'Il primo passo è semplicissimo: clicca su "Itaobuy Sheet" o "Buy It Now" dalla nostra homepage. Verrai reindirizzato allo spreadsheet completo con tutte le categorie già pronte da esplorare. Non serve registrazione né account: è tutto immediatamente accessibile.' },
                { step: 2, title: 'Scegli la categoria che ti interessa', desc: 'Lo spreadsheet è organizzato in 14 categorie: Scarpe, Felpe, T-Shirt, Giacche, Pantaloni, Cappelli e molte altre. Ogni categoria è una porta verso decine di prodotti selezionati. Scegli quella che fa per te e preparati a esplorare.' },
                { step: 3, title: 'Analizza le foto QC', desc: 'Questa è la fase più importante. Ogni prodotto include immagini QC — foto reali, senza filtri — che ti mostrano materiali, cuciture, dettagli e qualità effettiva. Prenditi il tempo per guardare ogni immagine con attenzione.' },
                { step: 4, title: 'Confronta i prodotti', desc: 'Uno dei vantaggi dello spreadsheet è la possibilità di confrontare più prodotti simili nella stessa categoria. Guarda prezzi, materiali e foto QC di diverse opzioni per trovare il miglior rapporto qualità-prezzo.' },
                { step: 5, title: 'Approfondisci con il link diretto', desc: 'Quando hai trovato il prodotto che ti convince, clicca sul link fornito per approfondire sulla piattaforma principale. Da lì potrai ottenere ulteriori informazioni e prendere la tua decisione finale.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 font-bold text-lg flex-shrink-0 mt-1">{item.step}</div>
                  <div>
                    <h3 className="font-bold text-warm-900 text-lg">{item.title}</h3>
                    <p className="mt-1 text-sm text-warm-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Consigli per <span className="text-tealwarm-600">principianti</span>
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-cream-50 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mb-3">
                  <i className="ri-search-eye-line text-lg"></i>
                </div>
                <h3 className="font-bold text-warm-900">Inizia da una categoria che conosci</h3>
                <p className="mt-1 text-sm text-warm-600">Se sei nuovo, parti da una categoria familiare come le T-Shirt. Impara a leggere le foto QC con prodotti che già conosci, poi esplora categorie nuove.</p>
              </div>
              <div className="bg-cream-50 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mb-3">
                  <i className="ri-bookmark-line text-lg"></i>
                </div>
                <h3 className="font-bold text-warm-900">Segna i prodotti che ti piacciono</h3>
                <p className="mt-1 text-sm text-warm-600">Tieni traccia dei prodotti interessanti mentre navighi. Puoi confrontarli più tardi con calma, quando hai visto tutte le opzioni disponibili.</p>
              </div>
              <div className="bg-cream-50 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="font-bold text-warm-900">Non avere fretta</h3>
                <p className="mt-1 text-sm text-warm-600">Prenditi il tempo necessario per valutare ogni opzione. Lo spreadsheet è uno strumento di esplorazione: più tempo dedichi, migliore sarà la scelta finale.</p>
              </div>
              <div className="bg-cream-50 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mb-3">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <h3 className="font-bold text-warm-900">Leggi la guida alla sicurezza</h3>
                <p className="mt-1 text-sm text-warm-600">Prima di qualsiasi acquisto, dai un&apos;occhiata alla nostra guida sulla sicurezza. Pochi minuti di lettura possono fare una grande differenza.</p>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Come si inizia a usare Itaobuy Spreadsheet?', a: 'Apri lo spreadsheet dal pulsante Itaobuy Sheet, scegli una categoria prodotto, esplora le schede con foto QC e link. È un processo intuitivo che richiede pochi minuti.' },
                { q: 'Devo creare un account?', a: 'No, puoi accedere allo spreadsheet e navigare tra le categorie senza registrazione.' },
                { q: 'Quanto tempo ci vuole per imparare?', a: 'Bastano 5-10 minuti per il funzionamento base. Con la pratica diventerai rapidissimo.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-warm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Pronto a <span className="text-tealwarm-600">iniziare</span>?
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Apri Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/itaobuy-spreadsheet-guida-completa" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-warm-300 text-warm-800 text-sm font-semibold hover:bg-warm-50 transition-all whitespace-nowrap cursor-pointer">
                Guida completa
              </Link>
            </div>
            <p className="mt-5">
              <Link to="/" className="text-sm text-tealwarm-700 hover:text-tealwarm-800 font-medium transition-colors">
                <i className="ri-arrow-left-line mr-1"></i> Torna alla homepage
              </Link>
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}